import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useV3OfferContext, V3_ACTIONS } from '../contexts/V3OfferContext';
import { actionErrorText } from '../../../shared/toastHelper';

// O-2783 "pool over offers" API hook. Owns the three pool mutations:
//
//   * enterPool()        — flip the group into Pool mode. POSTs to find-or-create
//                          the pool for the active offer's group (if none exists),
//                          or PATCHes mode→pool when the pool already exists.
//   * exitPool()         — PATCH mode→independent (the pool stays as a record but
//                          goes inert; offers behave exactly as before).
//   * updatePoolDeal()   — PATCH the pool's shared deal terms.
//
// All three merge the returned pool blueprint back onto the active offer's
// `v3_offer_pool` (+ `v3_offer_pool_id`) via UPDATE_OFFER so the toggle and the
// pool deal section re-render against fresh server state. The membership scalars
// (pool_percentage / pool_payout_strategy) persist through the existing offer
// autosave, not here.

const ERROR_TOAST_OPTS = {
  position: toast.POSITION.TOP_CENTER,
  draggable: false,
  hideProgressBar: true,
};

export default function useV3OfferPool() {
  // Tolerate a missing V3OfferProvider (bare test renders of the toggle): the
  // hook returns inert handlers and the gated component renders nothing.
  const ctx = useV3OfferContext() || {};
  const { csrfToken, team, offer, dispatch } = ctx;
  const [isSavingPool, setIsSavingPool] = useState(false);

  const setHeaders = useCallback(() => {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
  }, [csrfToken]);

  // Merge the pool blueprint the server returns onto the active offer.
  const applyPool = useCallback((pool) => {
    dispatch({
      type: V3_ACTIONS.UPDATE_OFFER,
      payload: { v3_offer_pool: pool || null, v3_offer_pool_id: pool?.id ?? null },
    });
  }, [dispatch]);

  // Latest offer/team/csrf in a ref so the debounced pool-deal saver
  // (updatePoolDeal) keeps a STABLE identity. If updatePoolDeal depended on
  // `offer`, every context change would recreate it → recreate
  // V3PoolDealSection's debounced save → its cleanup .cancel()s the in-flight
  // PATCH, so the pool deal guarantee never persisted (O-2783 data loss).
  const liveRef = useRef({ offer, team, csrfToken });
  useEffect(() => {
    liveRef.current = { offer, team, csrfToken };
  }, [offer, team, csrfToken]);

  const enterPool = useCallback(async () => {
    if (!offer?.id) return null;
    setHeaders();
    setIsSavingPool(true);
    try {
      let pool;
      if (offer.v3_offer_pool?.id) {
        const { data } = await axios.patch(
          `/teams/${team.id}/offer_pools/v3/${offer.v3_offer_pool.id}`,
          { v3_offer_pool: { mode: 'pool' } },
        );
        pool = data;
      } else {
        const { data } = await axios.post(
          `/teams/${team.id}/offer_pools/v3`,
          { v3_offer_id: offer.id, mode: 'pool' },
        );
        pool = data;
      }
      applyPool(pool);
      return pool;
    } catch (e) {
      toast.error(actionErrorText(e, 'Could not switch to pool mode'), ERROR_TOAST_OPTS);
      return null;
    } finally {
      setIsSavingPool(false);
    }
  }, [offer, team, setHeaders, applyPool]);

  // Leave Pool mode (back to Independent). Only meaningful when a pool exists.
  const exitPool = useCallback(async () => {
    if (!offer?.v3_offer_pool?.id) {
      // No pool record yet — nothing to update; the UI default is independent.
      applyPool(offer?.v3_offer_pool ? { ...offer.v3_offer_pool, mode: 'independent' } : null);
      return null;
    }
    setHeaders();
    setIsSavingPool(true);
    try {
      const { data } = await axios.patch(
        `/teams/${team.id}/offer_pools/v3/${offer.v3_offer_pool.id}`,
        { v3_offer_pool: { mode: 'independent' } },
      );
      applyPool(data);
      return data;
    } catch (e) {
      toast.error('Could not switch to independent mode', ERROR_TOAST_OPTS);
      return null;
    } finally {
      setIsSavingPool(false);
    }
  }, [offer, team, setHeaders, applyPool]);

  // Toggle handler for the segmented control.
  const setMode = useCallback((mode) => (
    mode === 'pool' ? enterPool() : exitPool()
  ), [enterPool, exitPool]);

  // PATCH the pool's shared deal terms. `attrs` is a partial of the pool's
  // permitted columns (dollar_amount, upside_percentage, operator, revenue_base,
  // breakeven_threshold, pool_payout_cap, pool_payout_floor, from_dollar_one).
  // Reads offer/team/csrf from a ref so this callback stays STABLE — see liveRef
  // above; a stable identity is what keeps V3PoolDealSection's debounced PATCH
  // from being cancelled mid-flight.
  const updatePoolDeal = useCallback(async (attrs) => {
    const { offer: o, team: t, csrfToken: c } = liveRef.current;
    const poolId = o?.v3_offer_pool?.id;
    if (!poolId) return null;
    axios.defaults.headers.common['X-CSRF-TOKEN'] = c;
    setIsSavingPool(true);
    try {
      const { data } = await axios.patch(
        `/teams/${t.id}/offer_pools/v3/${poolId}`,
        { v3_offer_pool: attrs },
      );
      applyPool(data);
      return data;
    } catch (e) {
      toast.error('Failed to save pool deal', ERROR_TOAST_OPTS);
      return null;
    } finally {
      setIsSavingPool(false);
    }
  }, [applyPool]);

  return {
    isSavingPool,
    enterPool,
    exitPool,
    setMode,
    updatePoolDeal,
  };
}
